import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { 
  Bug, 
  Camera, 
  X, 
  Send, 
  Loader2, 
  AlertCircle,
  User,
  CheckCircle,
  LogOut
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import html2canvas from 'html2canvas';

const API = `${window.location.origin}/api`;

const QATestingBanner = ({ session, onEndSession }) => {
  const [showBugForm, setShowBugForm] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [screenshot, setScreenshot] = useState(null);
  
  const [bugForm, setBugForm] = useState({
    title: '',
    description: '',
    steps_to_reproduce: '',
    expected_behavior: '',
    actual_behavior: '',
    priority: 'medium',
    role_tested: 'owner'
  });

  const captureScreenshot = async () => {
    setCapturing(true);
    try {
      // Hide the bug form temporarily for clean screenshot
      const bugFormEl = document.getElementById('qa-bug-form');
      if (bugFormEl) bugFormEl.style.display = 'none';
      
      const canvas = await html2canvas(document.body, {
        useCORS: true,
        allowTaint: true,
        scale: 0.75, // Reduce size
        logging: false
      });
      
      // Restore bug form
      if (bugFormEl) bugFormEl.style.display = 'block';
      
      const base64 = canvas.toDataURL('image/jpeg', 0.7);
      setScreenshot(base64);
      toast.success('Screenshot captured!');
    } catch (error) {
      console.error('Screenshot error:', error);
      toast.error('Failed to capture screenshot');
    } finally {
      setCapturing(false);
    }
  };

  const submitBugReport = async () => {
    if (!bugForm.title.trim()) {
      toast.error('Please enter a bug title');
      return;
    }
    
    if (!screenshot) {
      toast.error('Please capture a screenshot first');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/qa/bugs`, {
        ...bugForm,
        testing_as_customer: session?.customer_user_id,
        testing_as_email: session?.customer_email,
        session_id: session?.session_id,
        page_url: window.location.href,
        page_name: document.title,
        screenshot_base64: screenshot,
        browser_info: navigator.userAgent,
        screen_resolution: `${window.screen.width}x${window.screen.height}`
      }, { withCredentials: true });
      
      toast.success('Bug report submitted!');
      setShowBugForm(false);
      setBugForm({
        title: '',
        description: '',
        steps_to_reproduce: '',
        expected_behavior: '',
        actual_behavior: '',
        priority: 'medium',
        role_tested: 'owner'
      });
      setScreenshot(null);
    } catch (error) {
      toast.error('Failed to submit bug report');
    } finally {
      setSubmitting(false);
    }
  };

  const endSession = async () => {
    try {
      await axios.post(`${API}/qa/impersonate/end`, {
        session_id: session?.session_id
      }, { withCredentials: true });
      onEndSession();
      toast.success('Testing session ended');
    } catch (error) {
      console.error('End session error:', error);
    }
  };

  return (
    <>
      {/* Testing Banner - Fixed at top */}
      <div className="fixed top-0 left-0 right-0 z-[9999] bg-orange-500 text-white px-4 py-2">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-orange-600 px-3 py-1 rounded-full">
              <User className="w-4 h-4" />
              <span className="text-sm font-medium">QA Testing Mode</span>
            </div>
            <span className="text-sm">
              Testing as: <strong>{session?.customer_email}</strong> ({session?.customer_name || 'Customer'})
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              size="sm" 
              variant="secondary"
              className="bg-white text-orange-600 hover:bg-orange-50"
              onClick={() => setShowBugForm(true)}
            >
              <Bug className="w-4 h-4 mr-2" /> Report Bug
            </Button>
            <Button 
              size="sm" 
              variant="ghost"
              className="text-white hover:bg-orange-600"
              onClick={endSession}
            >
              <LogOut className="w-4 h-4 mr-2" /> End Testing
            </Button>
          </div>
        </div>
      </div>

      {/* Bug Report Modal */}
      {showBugForm && (
        <div className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-4">
          <Card id="qa-bug-form" className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Bug className="w-5 h-5 text-red-500" />
                  Report Bug
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowBugForm(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-sm text-zinc-500">
                Testing as: {session?.customer_email}
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Screenshot Section */}
              <div className="border rounded-lg p-4 bg-zinc-50">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-medium flex items-center gap-2">
                    <Camera className="w-4 h-4" /> Screenshot
                    {!screenshot && <span className="text-red-500 text-xs">(Required)</span>}
                  </span>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={captureScreenshot}
                    disabled={capturing}
                  >
                    {capturing ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Capturing...</>
                    ) : screenshot ? (
                      <><Camera className="w-4 h-4 mr-2" /> Retake</>
                    ) : (
                      <><Camera className="w-4 h-4 mr-2" /> Capture Screen</>
                    )}
                  </Button>
                </div>
                {screenshot ? (
                  <div className="relative">
                    <img 
                      src={screenshot} 
                      alt="Bug screenshot" 
                      className="w-full rounded border"
                    />
                    <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-1 rounded text-xs flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" /> Captured
                    </div>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-zinc-300 rounded-lg p-8 text-center text-zinc-500">
                    <AlertCircle className="w-8 h-8 mx-auto mb-2 text-orange-500" />
                    <p className="text-sm">Click "Capture Screen" to take a screenshot</p>
                    <p className="text-xs text-zinc-400 mt-1">This helps us understand the issue better</p>
                  </div>
                )}
              </div>

              {/* Bug Details */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Priority</label>
                  <select 
                    className="w-full border rounded px-3 py-2 text-sm"
                    value={bugForm.priority}
                    onChange={(e) => setBugForm({...bugForm, priority: e.target.value})}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Role Being Tested</label>
                  <select 
                    className="w-full border rounded px-3 py-2 text-sm"
                    value={bugForm.role_tested}
                    onChange={(e) => setBugForm({...bugForm, role_tested: e.target.value})}
                  >
                    <option value="owner">Owner</option>
                    <option value="editor">Editor</option>
                    <option value="viewer">Viewer</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">Bug Title *</label>
                <Input 
                  placeholder="Brief description of the issue"
                  value={bugForm.title}
                  onChange={(e) => setBugForm({...bugForm, title: e.target.value})}
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">Description</label>
                <Textarea 
                  placeholder="Detailed description of what went wrong"
                  value={bugForm.description}
                  onChange={(e) => setBugForm({...bugForm, description: e.target.value})}
                  rows={3}
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">Steps to Reproduce</label>
                <Textarea 
                  placeholder="1. Go to...&#10;2. Click on...&#10;3. See error"
                  value={bugForm.steps_to_reproduce}
                  onChange={(e) => setBugForm({...bugForm, steps_to_reproduce: e.target.value})}
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Expected Behavior</label>
                  <Textarea 
                    placeholder="What should happen"
                    value={bugForm.expected_behavior}
                    onChange={(e) => setBugForm({...bugForm, expected_behavior: e.target.value})}
                    rows={2}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Actual Behavior</label>
                  <Textarea 
                    placeholder="What actually happened"
                    value={bugForm.actual_behavior}
                    onChange={(e) => setBugForm({...bugForm, actual_behavior: e.target.value})}
                    rows={2}
                  />
                </div>
              </div>

              {/* Auto-captured info */}
              <div className="bg-zinc-100 rounded p-3 text-xs text-zinc-600">
                <p><strong>Page:</strong> {window.location.pathname}</p>
                <p><strong>Browser:</strong> {navigator.userAgent.split(' ').slice(-2).join(' ')}</p>
                <p><strong>Screen:</strong> {window.screen.width}x{window.screen.height}</p>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowBugForm(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={submitBugReport} 
                  disabled={submitting || !screenshot}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
                  ) : (
                    <><Send className="w-4 h-4 mr-2" /> Submit Bug Report</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
};

export default QATestingBanner;
